import { memo } from "react";
import dynamic from "next/dynamic";
import cx from "classnames";

import type { IListComponentProps } from "../../types";

import styles from "./GridComponent.module.scss";
import { CellType } from "@nf/common";

const ListComponentItem = dynamic(
  () =>
    import(
      "components/Configuration/ListComponent/components/ListComponentItem/components/ListComponentItem"
    )
);

type GridBreakpoint = {
  cols?: number;
  gap?: string;
};

interface GridResponsiveConfig {
  phone: GridBreakpoint;
  tablet?: GridBreakpoint;
  desktop?: GridBreakpoint;
  large?: GridBreakpoint;
}

export interface GridComponentProps extends IListComponentProps {
  className?: string;
  responsive?: GridResponsiveConfig;
}

export const GridComponent = memo(
  ({ className, responsive, component, loading }: GridComponentProps) => {
    const {
      phone = {},
      tablet = {},
      desktop = {},
      large = {},
    } = responsive || {};

    const isCover =
      component.CellType === CellType.Cover ||
      component.CellType === CellType.CoverLarge;
    const entities = component.MediaList;

    // Fallback logic
    const cols = {
      phone: phone.cols ?? (isCover ? 3 : 2),
      tablet: tablet.cols ?? (phone.cols ?? isCover ? 3 : 2),
      desktop: desktop.cols ?? tablet.cols ?? phone.cols ?? (isCover ? 4 : 3),
      large:
        large.cols ??
        desktop.cols ??
        tablet.cols ??
        phone.cols ??
        (isCover ? 6 : 3),
    };

    const gaps = {
      phone: phone.gap ?? "8px",
      tablet: tablet.gap ?? phone.gap ?? "8px",
      desktop: desktop.gap ?? tablet.gap ?? phone.gap ?? "16px",
      large: large.gap ?? desktop.gap ?? tablet.gap ?? phone.gap ?? "16px",
    };

    return (
      <div
        className={cx(styles.grid, className)}
        style={
          {
            "--cols": cols.phone,
            "--cols-tablet": cols.tablet,
            "--cols-desktop": cols.desktop,
            "--cols-large": cols.large,

            "--gap": gaps.phone,
            "--gap-tablet": gaps.tablet,
            "--gap-desktop": gaps.desktop,
            "--gap-large": gaps.large,
          } as React.CSSProperties
        }
      >
        {entities?.map((media) => (
          <ListComponentItem
            key={media.Id}
            media={media}
            cellType={component.CellType}
            cutomListItem={component.CutomListItem}
            isPlaceholder={!!loading}
            readOnly={loading}
            query={component.QueryParams}
            renderSSR={true}
          />
        ))}
      </div>
    );
  }
);

export default GridComponent;
